import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Checkbox, Chip, CircularProgress, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { useTranslation } from 'react-i18next';
import { CategoryNode, TYPE_HAS_CHECKBOX, TYPE_LABEL } from '../growCategoryRestriction.types';
import {
    collectExpandableIds,
    countLeafNodes,
    filterRestrictionTree,
    RestrictionFilter,
} from '../growCategoryRestriction.helpers';
import { ICON_COLOR, LEVEL_TAG_SX, LevelIcon } from '../growCategoryRestriction.visual';
import {
    getRestrictionRowStyles,
    getRestrictionTypographySx,
    renderCategoryExpandIcon,
    ChildCountBadge,
} from '../growCategoryRestriction.rowHelpers';
import { GCR } from '../growCategoryRestriction.classes';

// ─── Restriction-tree row ─────────────────────────────────────────────────────

interface RestrictionRowProps {
    node: CategoryNode;
    depth: number;
    isExpanded: boolean;
    restrictedIds: Set<number>;
    onToggleExpand: (node: CategoryNode) => void;
    onToggleRestrict: (node: CategoryNode, checked: boolean) => void;
}

const RestrictionRow = ({
    node,
    depth,
    isExpanded,
    restrictedIds,
    onToggleExpand,
    onToggleRestrict,
}: RestrictionRowProps) => {
    const { t } = useTranslation();
    const hasCheckbox = TYPE_HAS_CHECKBOX[node.typeName];
    const hasChildren = !!(node.children && node.children.length > 0);
    const isRestricted = restrictedIds.has(node.categoryId);
    const color = ICON_COLOR[node.typeName];
    const childCount = isExpanded && hasChildren ? node.children!.length : 0;
    const rowStyles = getRestrictionRowStyles(isRestricted, color, hasChildren);

    return (
        <Box
            onClick={() => {
                if (hasChildren) onToggleExpand(node);
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                pl: `${depth * 14 + 4}px`,
                pr: 1,
                py: '4px',
                mx: '2px',
                my: '1px',
                borderRadius: '7px',
                minHeight: 30,
                cursor: rowStyles.cursor,
                backgroundColor: rowStyles.backgroundColor,
                borderLeft: rowStyles.borderLeft,
                transition: 'background-color 0.15s, border-left-color 0.15s',
                '&:hover': {
                    backgroundColor: rowStyles.hoverBackgroundColor,
                    borderLeftColor: rowStyles.hoverBorderLeftColor,
                },
            }}
        >
            <Box
                sx={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isExpanded ? 'error.main' : 'text.secondary',
                }}
            >
                {renderCategoryExpandIcon(hasChildren, isExpanded)}
            </Box>

            {hasCheckbox ? (
                <Checkbox
                    size="small"
                    checked={isRestricted}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(_, checked) => onToggleRestrict(node, checked)}
                    sx={{
                        p: '1px',
                        flexShrink: 0,
                        color: 'text.disabled',
                        '&.Mui-checked': { color: 'error.main' },
                        '& .MuiSvgIcon-root': { fontSize: 15 },
                    }}
                />
            ) : (
                <Box sx={{ width: 20, flexShrink: 0 }} />
            )}

            <LevelIcon typeName={node.typeName} />

            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                <Tooltip
                    title={!node.isActive ? t('growCategoryRestriction.inactiveCategory') : node.description}
                    enterDelay={!node.isActive ? 200 : 700}
                    disableInteractive
                >
                    <Typography
                        noWrap
                        sx={getRestrictionTypographySx(node, isRestricted)}
                    >
                        {node.description}
                    </Typography>
                </Tooltip>

                <ChildCountBadge count={childCount} />
            </Box>

            <Chip
                label={TYPE_LABEL[node.typeName]}
                size="small"
                sx={{
                    ...(LEVEL_TAG_SX[node.typeName] as object),
                    minWidth: 28,
                    borderRadius: '5px',
                    flexShrink: 0,
                }}
            />
        </Box>
    );
};

// ─── Recursive restriction branch ────────────────────────────────────────────

interface RestrictionBranchProps {
    nodes: CategoryNode[];
    depth: number;
    expandedIds: Set<number>;
    restrictedIds: Set<number>;
    onToggleExpand: (node: CategoryNode) => void;
    onToggleRestrict: (node: CategoryNode, checked: boolean) => void;
}

const RestrictionBranch = ({
    nodes,
    depth,
    expandedIds,
    restrictedIds,
    onToggleExpand,
    onToggleRestrict,
}: RestrictionBranchProps) => (
    <>
        {nodes.map((node) => {
            const isExpanded = expandedIds.has(node.categoryId);
            const hasChildren = !!(node.children && node.children.length > 0);

            return (
                <Box key={node.categoryId}>
                    <RestrictionRow
                        node={node}
                        depth={depth}
                        isExpanded={isExpanded}
                        restrictedIds={restrictedIds}
                        onToggleExpand={onToggleExpand}
                        onToggleRestrict={onToggleRestrict}
                    />
                    {isExpanded && hasChildren && (
                        <RestrictionBranch
                            nodes={node.children!}
                            depth={depth + 1}
                            expandedIds={expandedIds}
                            restrictedIds={restrictedIds}
                            onToggleExpand={onToggleExpand}
                            onToggleRestrict={onToggleRestrict}
                        />
                    )}
                </Box>
            );
        })}
    </>
);

// ─── Main component ───────────────────────────────────────────────────────────

interface ActiveRestrictionsProps {
    restrictionTree: CategoryNode[];
    restrictedIds: Set<number>;
    onToggleRestrict: (node: CategoryNode, checked: boolean) => void;
    isRestricting?: boolean;
}

export const ActiveRestrictions = ({
    restrictionTree,
    restrictedIds,
    onToggleRestrict,
    isRestricting = false,
}: ActiveRestrictionsProps) => {
    const { t } = useTranslation();
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const [filterMode, setFilterMode] = useState<RestrictionFilter>('all');
    const knownExpandableRef = useRef<Set<number>>(new Set());

    const filteredTree = useMemo(
        () => filterRestrictionTree(restrictionTree, filterMode, restrictedIds),
        [restrictionTree, filterMode, restrictedIds]
    );

    // Auto-expand newly added parents; preserve user collapse/expand otherwise.
    useEffect(() => {
        const expandable = collectExpandableIds(restrictionTree);

        setExpandedIds((prev) => {
            if (restrictionTree.length === 0) return new Set();

            const next = new Set<number>();
            prev.forEach((id) => {
                if (expandable.has(id)) next.add(id);
            });
            expandable.forEach((id) => {
                if (!knownExpandableRef.current.has(id)) next.add(id);
            });
            return next;
        });

        knownExpandableRef.current = expandable;
    }, [restrictionTree]);

    // When a filter is active, expand the filtered branches so matches are visible.
    useEffect(() => {
        if (filterMode === 'all') return;
        setExpandedIds(collectExpandableIds(filteredTree));
    }, [filterMode, filteredTree]);

    const handleToggleExpand = useCallback((node: CategoryNode) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(node.categoryId)) next.delete(node.categoryId);
            else next.add(node.categoryId);
            return next;
        });
    }, []);

    const handleCollapseAll = useCallback(() => {
        setExpandedIds(new Set());
    }, []);

    const handleFilterToggle = useCallback((mode: Exclude<RestrictionFilter, 'all'>) => {
        setFilterMode((prev) => (prev === mode ? 'all' : mode));
    }, []);

    const totalItems = countLeafNodes(filteredTree);
    const checkedCount = filterMode === 'active' ? 0 : filterMode === 'restricted' ? totalItems : restrictedIds.size;

    return (
        <Paper
            elevation={0}
            className={GCR.restrictionsPanel}
            sx={{
                flex: '0 0 30%',
                width: '30%',
                borderRadius: '12px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                border: '1px solid rgba(239,68,68,0.15)',
                boxShadow: '0 2px 16px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid rgba(239,68,68,0.1)',
                    background: 'linear-gradient(135deg, #fff5f5 0%, #fafafa 100%)',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '8px',
                            bgcolor: '#ef4444',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 6px rgba(239,68,68,0.35)',
                        }}
                    >
                        <LockIcon sx={{ fontSize: 14, color: '#fff' }} />
                    </Box>
                    <Typography variant="subtitle2" fontWeight={700} fontSize={13.5} letterSpacing="0.01em">
                        {t('growCategoryRestriction.restrictions.title')}
                    </Typography>

                    {restrictionTree.length > 0 && (
                        <Button
                            size="small"
                            onClick={handleCollapseAll}
                            startIcon={<UnfoldLessIcon sx={{ fontSize: '14px !important' }} />}
                            sx={{
                                height: 32,
                                minWidth: 100,
                                px: 1.5,
                                border: '1px solid rgba(0,0,0,0.12)',
                                borderRadius: '6px',
                                color: 'text.secondary',
                                textTransform: 'none',
                                fontSize: 10,
                                mr: 1,
                                fontWeight: 600,
                                '& .MuiButton-startIcon': { mr: 0.75, ml: -0.25 },
                                transition: 'all 0.15s',
                                '&:hover': {
                                    borderColor: '#ef4444',
                                    color: '#ef4444',
                                    backgroundColor: 'rgba(239,68,68,0.05)',
                                },
                            }}
                        >
                            {t('growCategoryRestriction.hierarchy.collapseAll')}
                        </Button>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {checkedCount > 0 && (
                        <Chip
                            label={t('growCategoryRestriction.restrictions.checkedCount', {
                                count: checkedCount,
                            })}
                            size="small"
                            sx={{
                                bgcolor: '#fee2e2',
                                color: '#b91c1c',
                                fontWeight: 700,
                                fontSize: 11,
                                height: 22,
                                borderRadius: '6px',
                                border: '1px solid #fca5a5',
                                minWidth: 28,
                            }}
                        />
                    )}
                    {totalItems > 0 && (
                        <Chip
                            label={t('growCategoryRestriction.restrictions.totalItems', {
                                count: totalItems,
                            })}
                            size="small"
                            sx={{
                                bgcolor: '#f5f5f5',
                                color: '#555',
                                fontWeight: 600,
                                fontSize: 11,
                                height: 22,
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0',
                                minWidth: 28,
                            }}
                        />
                    )}
                </Box>
            </Box>

            {restrictionTree.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 1,
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        bgcolor: '#fafafa',
                        flexShrink: 0,
                    }}
                >
                    <Tooltip title={t('growCategoryRestriction.restricted')}>
                        <IconButton
                            size="small"
                            onClick={() => handleFilterToggle('restricted')}
                            aria-label={t('growCategoryRestriction.restricted')}
                            aria-pressed={filterMode === 'restricted'}
                            sx={{
                                borderRadius: '8px',
                                px: 1,
                                gap: 0.5,
                                border:
                                    filterMode === 'restricted' ? '1px solid #fca5a5' : '1px solid rgba(0,0,0,0.12)',
                                bgcolor: filterMode === 'restricted' ? '#fee2e2' : 'transparent',
                                color: filterMode === 'restricted' ? '#b91c1c' : 'text.secondary',
                                '&:hover': {
                                    bgcolor: filterMode === 'restricted' ? '#fecaca' : 'rgba(239,68,68,0.06)',
                                    borderColor: '#fca5a5',
                                    color: '#b91c1c',
                                },
                            }}
                        >
                            <LockIcon sx={{ fontSize: 15 }} />
                            <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 700 }}>
                                {t('growCategoryRestriction.restricted')}
                            </Typography>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={t('growCategoryRestriction.restrictions.filterActive')}>
                        <IconButton
                            size="small"
                            onClick={() => handleFilterToggle('active')}
                            aria-label={t('growCategoryRestriction.restrictions.filterActive')}
                            aria-pressed={filterMode === 'active'}
                            sx={{
                                borderRadius: '8px',
                                px: 1,
                                gap: 0.5,
                                border: filterMode === 'active' ? '1px solid #86efac' : '1px solid rgba(0,0,0,0.12)',
                                bgcolor: filterMode === 'active' ? '#dcfce7' : 'transparent',
                                color: filterMode === 'active' ? '#15803d' : 'text.secondary',
                                '&:hover': {
                                    bgcolor: filterMode === 'active' ? '#bbf7d0' : 'rgba(34,197,94,0.06)',
                                    borderColor: '#86efac',
                                    color: '#15803d',
                                },
                            }}
                        >
                            <LockOpenIcon sx={{ fontSize: 15 }} />
                            <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 700 }}>
                                {t('growCategoryRestriction.restrictions.filterActive')}
                            </Typography>
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 1, position: 'relative' }}>
                {isRestricting && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: 'rgba(255,255,255,0.55)',
                            backdropFilter: 'blur(1.5px)',
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'all',
                        }}
                    >
                        <CircularProgress size={36} thickness={3.5} color="error" />
                    </Box>
                )}

                {restrictionTree.length === 0 ? (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            py: 4,
                        }}
                    >
                        <LockOpenIcon sx={{ fontSize: 36, color: 'text.disabled', opacity: 0.4 }} />
                        <Typography
                            variant="body2"
                            color="text.disabled"
                            fontWeight={500}
                            fontSize={12}
                            textAlign="center"
                        >
                            {t('growCategoryRestriction.restrictions.placeholder')}
                        </Typography>
                    </Box>
                ) : filteredTree.length === 0 ? (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            py: 4,
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.disabled"
                            fontWeight={500}
                            fontSize={12}
                            textAlign="center"
                        >
                            {t('growCategoryRestriction.restrictions.noMatch')}
                        </Typography>
                    </Box>
                ) : (
                    <RestrictionBranch
                        nodes={filteredTree}
                        depth={0}
                        expandedIds={expandedIds}
                        restrictedIds={restrictedIds}
                        onToggleExpand={handleToggleExpand}
                        onToggleRestrict={onToggleRestrict}
                    />
                )}
            </Box>
        </Paper>
    );
};
